<template>
    <div class="onboarding modal flex">
        <transition
            name="fade"
            mode="out-in"
        >
            <error v-if="hasError || hasRequestError" />
            <div v-else-if="isLoading">
                <i class="fas fa-spinner fa-spin loader" />
            </div>
            <welcome
                v-else-if="0 === step"
                ref="question"
                :step="step"
            />
            <template
                v-else-if="step <= profileControls.length"
            >
                <keep-alive>
                    <question
                        :key="`question-${ step }`"
                        ref="question"
                        :step="step"
                        :total-steps="profileControls.length"
                        :control="profileControls[step - 1]"
                    />
                </keep-alive>
            </template>
            <thank-you
                v-else
                :step="step"
            />
        </transition>
    </div>
</template>

<script>
import Error from './Onboarding/Error.vue';
import Question from './Onboarding/Question.vue';
import ThankYou from './Onboarding/ThankYou.vue';
import Welcome from './Onboarding/Welcome.vue';

import { mapState, mapActions, mapMutations } from 'vuex';

import overflowBlocker from '@/utils/overflowBlocker';
import apiCall from '@/utils/apiCall.js';

export default {
    components: {
        Error,
        Question,
        ThankYou,
        Welcome,
    },

    data: () => ({
        step: null,
        hasRequestError: false,
    }),

    computed: {
        ...mapState([
            'user',
            'profileControls',
        ]),

        ...mapState({
            isLoading: state => null === state.profileControls,
            hasError: state => false === state.profileControls,
        }),
    },

    /**
     * Created hook
     */
    created() {
        const {
            onboarding_started,
            onboarding_step,
        } = this.user.meta_data;

        this.step = onboarding_step || (onboarding_started ? 1 : 0);

        this.setControlsListeners();
        overflowBlocker(true);
    },

    /**
     * Before Destroy hook
     */
    beforeDestroy() {
        this.removeControlListeners();
    },

    methods: {
        ...mapActions([
            'setUserMeta',
            'deleteUserMeta',
        ]),

        ...mapMutations(['changeUIState']),

        /**
         * Set hasRequestError flag
         */
        setError() {
            this.hasRequestError = true;
        },

        /**
         * Sets listeners for onboarding controls
         */
        setControlsListeners() {
            this.$event.$on('skipOnboarding', this.skipOnboardingHandler);
            this.$event.$on('onboardingNextStep', this.nextStepHandler);
            this.$event.$on('onboardingPreviousStep', this.previousStepHandler);
        },

        /**
         * Remove onboarding controls listeners
         */
        removeControlListeners() {
            this.$event.$off('skipOnboarding', this.skipOnboardingHandler);
            this.$event.$off('onboardingNextStep', this.nextStepHandler);
            this.$event.$off('onboardingPreviousStep', this.previousStepHandler);
        },

        /**
         * Skip onboarding handler
         *
         */
        async skipOnboardingHandler() {
            if (0 === this.step || this.step > this.profileControls.length) {
                this.finishOnboarding();
                return;
            }

            this.$wait.start('onboardingProcessing');

            const meta = {
                onboarding_step: this.step + 1,
            };

            if (this.step === this.profileControls.length) {
                meta.onboarding_finished = Date.now();
            }

            try {
                await this.setUserMeta(meta);

                this.nextStep();
            } catch (error) {
                this.setError();
            }
        },

        /**
         * Next step handler
         */
        async nextStepHandler() {
            this.$wait.start('onboardingProcessing');

            try {
                if (0 === this.step) {
                    await this.setUserMeta({ onboarding_started: Date.now() });
                } else if (this.step <= this.profileControls.length) {
                    if (this.$refs.question._data.answer) {
                        await this.sendAnswer();
                    }
                } else {
                    await this.setUserMeta({ onboarding_finished: Date.now() });
                    this.finishOnboarding();
                    return;
                }

                this.nextStep();
            } catch (error) {
                this.setError();
            }
        },

        /**
         * Previous step handler
         *
         */
        previousStepHandler() {
            this.savePreviousStepInfo()
                .then(this.previousStep)
                .catch(this.setError);
        },

        /**
         * Changes step counter
         */
        nextStep() {
            this.$wait.end('onboardingProcessing');
            this.step++;
        },

        /**
         * Changes step counter
         */
        previousStep() {
            if (2 <= this.step) {
                this.$wait.end('onboardingProcessing');
                this.step--;
            }
        },

        /**
         * Saves user onboarding progress
         *
         * @returns {Promise} Save meta request.
         */
        savePreviousStepInfo() {
            if (2 <= this.step) {
                return apiCall(this, 'put', `${this.$appUrl}/api/user/meta`, { onboarding_step: this.step - 1 });
            }
        },

        /**
         * Sends meta to API
         *
         * @returns {Promise} API call promise.
         */
        async sendAnswer() {
            const { answer } = this.$refs.question._data;
            const metaKey = this.profileControls[this.step - 1].name;

            const meta = {
                [metaKey]: answer,
                onboarding_step: this.step + 1,
            };

            if (this.step === this.profileControls.length) {
                // on last step set flag that the onboarding is finished.
                meta.onboarding_finished = Date.now();

                this.deleteUserMeta(['onboarding_started', 'onboarding_step'])
                    .catch(this.setError);
            }

            return this.setUserMeta(meta);
        },

        /**
         * Finishes onboarding steps
         *
         */
        finishOnboarding() {
            this.changeUIState({ isOnboardingModalVisible: false });

            overflowBlocker(false);
        },
    },
};
</script>
